"use client";

import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import Pagination from "../../../components/admin/common/Pagination";
import { GalleryTable } from "../../../components/admin/gallery/GalleryTable";
import { getAllGalleries } from "../../../services/galleryServices";
import { getCookie } from "../../../utils";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";

const GalleryPage = () => {
  const [entriesOption, setEntriesOption] = useState({
    value: 8,
    label: "8",
  });
  const [lastIds, setLastIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryList, setGalleryList] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  // Ref for the header to scroll to
  const headerRef = useRef(null);

  const currentLastId =
    currentIndex > 0 ? lastIds[currentIndex - 1] : undefined;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["galleries", currentLastId, entriesOption.value],
    queryFn: () => getAllGalleries(currentLastId, entriesOption.value),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.data) {
      setGalleryList(data.data.data);
      setHasMore(data.data.hasMore);
    }
  }, [data]);

  // Fixed scroll to top when pagination changes
  useEffect(() => {
    // Scroll to the header section smoothly
    if (headerRef.current) {
      headerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      // Fallback: scroll to top of the window
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentIndex, entriesOption.value]);

  const handleNext = () => {
    if (hasMore && data?.data?.nextLastId) {
      const updated = [...lastIds];
      updated[currentIndex] = data.data.nextLastId;
      setLastIds(updated);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentIndex(0);
    setLastIds([]);
  };

  const userId = getCookie("userId");
  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions(
    "Gallery",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              {/* Header with ref for scroll target */}
              <div ref={headerRef}>
                <Header
                  title="Gallery List"
                  desc="Review and manage pending gallery submissions before they go live"
                />
              </div>

              <DropdownSelect
                selected={entriesOption}
                onChange={handleEntriesChange}
              />

              {isLoading ? (
                <p>Loading galleries...</p>
              ) : isError ? (
                <p>Failed to fetch galleries.</p>
              ) : (
                <GalleryTable
                  galleries={galleryList}
                  refetch={refetch}
                  startIndex={currentIndex * entriesOption.value}
                  permissionsData={permissionsData?.data ?? null}
                />
              )}

              <div className="h-5" />
              <Pagination
                currentPage={currentIndex + 1}
                hasMore={hasMore}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
              <div className="h-5" />
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default GalleryPage;
